import { resolveMembershipPricing } from '@/utils/v2MembershipPricing'

// The collapsed card's "from $X" and the product modal's running total must be built on the same
// number, or a customer sees one price on the menu and another in the modal. Both call this.
//
// The precedence mirrors the chain the inline card price used to render with, before BOT-1108
// removed it: an upsell price wins outright, then the price for the active
// pricing tier at the customer's membership tier, then the plain membership price, then the
// catalog price. Cart-line totals are deliberately NOT considered here - this is a unit price for
// one configuration, not the price of an existing cart line.
export function v2BasePriceCents(product, { pricingTierId = null, membershipTierId = null, isUpsell = false } = {}) {
  if (!product) return 0

  // An explicitly-set upsell price wins outright, above catalog as well as below:
  // UpsellCartItem#assign_net_prices_cents is `default_upsell_price_cents || super`, so the tier
  // and membership chain is only reached when the merchant set no upsell price at all. The old
  // `< price_cents` guard belonged to the old strike-through display, where a "was/now" only makes
  // sense cheaper - not to the number the customer is charged.
  //
  // `isUpsell` is the rendering CONTEXT, not a property of the product. The snapshot carries
  // `upsell_price_cents` on the product wherever it appears, so a product that is also offered as
  // an upsell would otherwise be priced at its upsell price on the ordinary store grid - the
  // divergence filed as F3 and fixed by merchant-vue 2544660. V1 gates the same way, on the
  // category behind the pair being an UpsellCategory.
  if (isUpsell && product.upsell_price_cents != null) {
    return Number(product.upsell_price_cents) || 0
  }

  const tierEntry = findPricingTierEntry(product, pricingTierId, membershipTierId)
  if (tierEntry && tierEntry.price_cents != null) return Number(tierEntry.price_cents) || 0

  const membership = resolveMembershipPricing(product, membershipTierId)
  if (membership) return Number(membership.membershipPriceCents) || 0

  return Number(product.price_cents) || 0
}

/**
 * What the product would have cost without the discount it is carrying - the number the card
 * strikes through. Null when there is nothing to strike.
 *
 * Mirrors v1DefaultPrice, and for the same reason: an upsell price only reads as an offer next to
 * the price it replaced. The customer's tier is compared against the DEFAULT tier's price rather
 * than the catalog price, so a store whose default tier already sits below catalog does not show a
 * permanent sale.
 */
export function v2DefaultPriceCents(
  product,
  {
    pricingTierId = null,
    membershipTierId = null,
    defaultPricingTierId = null,
    defaultMembershipTierId = null,
    isUpsell = false
  } = {}
) {
  if (!product) return null

  // Same context gate as the active price: outside the upsell screen this product is not on offer,
  // so there is no "was".
  if (isUpsell && product.upsell_price_cents != null) {
    const catalog = Number(product.price_cents) || 0
    return catalog > Number(product.upsell_price_cents) ? catalog : null
  }

  const active = v2BasePriceCents(product, { pricingTierId, membershipTierId, isUpsell })

  const defaultTierEntry =
    findPricingTierEntry(product, defaultPricingTierId, defaultMembershipTierId) ||
    findPricingTierEntry(product, defaultPricingTierId, membershipTierId)
  const candidates = [defaultTierEntry && defaultTierEntry.price_cents, product.price_cents]
    .filter((value) => value != null)
    .map(Number)
  if (!candidates.length) return null

  const highest = Math.max(...candidates)
  return highest > active ? highest : null
}

function findPricingTierEntry(product, pricingTierId, membershipTierId) {
  if (pricingTierId == null || membershipTierId == null) return null
  return (
    (product.pricing_tier_prices || []).find(
      (entry) =>
        String(entry.pricing_tier_id) === String(pricingTierId) &&
        String(entry.membership_tier_id) === String(membershipTierId)
    ) || null
  )
}
